/**
 * Action Pending: selectable questions (left), criteria and CRC actions (right).
 */

import { Tags, toast } from "@risalabs_frontend_org/oasis-ui-kit";
import { useAuth } from "@risalabs_frontend_org/auth-wrapper";
import { useEffect, useMemo, useState } from "react";
import type { NoteAssigneeResponse } from "../../../../api/clinicalTrialsApiTypes";
import { clinicalTrialsService } from "../../../../api/clinicalTrialsService";
import { useEligibilityDetailContext } from "../../EligibilityDetailContext";
import EvaluationSplitPane from "../../shared/EvaluationSplitPane";
import { ManualOverrideModal } from "../../shared/ManualOverrideModal";
import { screeningStatusColors } from "../../shared/screeningStatusDisplay";
import CrcActionCard from "./CrcActionCard";
import InclusionCriteriaAccordion from "./InclusionCriteriaAccordion";
import {
  buildCreateEligibilityTaskRequest,
  buildInclusionCriteriaGroups,
  mapRecommendedActionToCard,
  resolveTaskAssigneeId,
} from "./mapActionPendingContent";

/**
 * Render pending questions with their criteria and recommended CRC action cards.
 *
 * @returns Action-pending split layout
 */
const ActionPendingTab = () => {
  const { user } = useAuth();
  const { detail, trialId, mrn, refreshDetail } = useEligibilityDetailContext();
  const { recommended_actions: actions, overrides, latest_screening } = detail;
  const inclusionGroups = useMemo(
    () => buildInclusionCriteriaGroups(latest_screening, actions),
    [actions, latest_screening],
  );
  const [selectedQuestionId, setSelectedQuestionId] = useState(
    inclusionGroups[0]?.id ?? "",
  );

  const [checkedActionIds, setCheckedActionIds] = useState<Set<string>>(
    () => new Set(),
  );
  const [overrideAtomId, setOverrideAtomId] = useState<string | null>(null);
  const [taskSubmitting, setTaskSubmitting] = useState(false);
  const [assignees, setAssignees] = useState<NoteAssigneeResponse[]>([]);
  const [assigneesLoading, setAssigneesLoading] = useState(true);

  useEffect(() => {
    setSelectedQuestionId((current) => {
      if (inclusionGroups.some((group) => group.id === current)) {
        return current;
      }
      return inclusionGroups[0]?.id ?? "";
    });
  }, [inclusionGroups]);

  const selectedGroup = inclusionGroups.find(
    (group) => group.id === selectedQuestionId,
  );
  const selectedActions = useMemo(
    () =>
      actions.filter((action) => action.question_id === selectedQuestionId),
    [actions, selectedQuestionId],
  );
  const selectedActionCards = useMemo(
    () =>
      selectedActions.map((action) =>
        mapRecommendedActionToCard(action, latest_screening),
      ),
    [latest_screening, selectedActions],
  );
  const pendingColors = screeningStatusColors("Action Pending");
  const listItems = inclusionGroups.map((group, index) => ({
    id: group.id,
    positionText: `#${index + 1}`,
    criteriaDetails: group.title,
    tagLabel: "Action Pending",
    tagBackgroundColor: pendingColors.backgroundColor,
    tagTextColor: pendingColors.textColor,
  }));

  useEffect(() => {
    const controller = new AbortController();
    setAssigneesLoading(true);
    clinicalTrialsService
      .getNoteAssignees(controller.signal)
      .then((members) => {
        if (!controller.signal.aborted) {
          setAssignees(members);
        }
      })
      .catch(() => {
        if (!controller.signal.aborted) {
          setAssignees([]);
        }
      })
      .finally(() => {
        if (!controller.signal.aborted) {
          setAssigneesLoading(false);
        }
      });
    return () => controller.abort();
  }, []);

  const taskAssigneeId = useMemo(() => {
    const fromDirectory = resolveTaskAssigneeId(assignees);
    if (fromDirectory) {
      return fromDirectory;
    }
    if (!user?.id) {
      return null;
    }
    return (
      assignees.find((member) => member.user_id === user.id)?.user_id ?? null
    );
  }, [assignees, user?.id]);

  const activeOverride = overrides.find(
    (item) => item.atom_id === overrideAtomId,
  );
  const activeAtomStatus =
    actions.find((action) => action.atom_id === overrideAtomId)?.status ?? "";

  /**
   * Toggle completion checkbox for one recommended action row.
   *
   * @param actionId - Server action identifier
   * @param checked - Next checked state
   */
  const handleActionChecked = (actionId: string, checked: boolean) => {
    setCheckedActionIds((previous) => {
      const next = new Set(previous);
      if (checked) {
        next.add(actionId);
      } else {
        next.delete(actionId);
      }
      return next;
    });
  };

  const hasCheckedAction = selectedActions.some((action) =>
    checkedActionIds.has(action.action_id),
  );

  /**
   * First checked recommended action in server order.
   */
  const selectedCheckedAction = () =>
    selectedActions.find((action) => checkedActionIds.has(action.action_id)) ??
    null;

  /**
   * Open manual override for the first checked action, or the first action row.
   */
  const handleManualOverrideClick = () => {
    const targetAtomId =
      selectedCheckedAction()?.atom_id ?? selectedActions[0]?.atom_id ?? null;
    if (targetAtomId) {
      setOverrideAtomId(targetAtomId);
    }
  };

  /**
   * Create a linked task (or record a schedule match) for the selected action.
   */
  const handleTaskInitiatedClick = async () => {
    const targetAction = selectedCheckedAction();
    if (!targetAction) {
      return;
    }
    if (!taskAssigneeId) {
      toast.error("No eligible assignee is available for this task.");
      return;
    }
    setTaskSubmitting(true);
    try {
      const response = await clinicalTrialsService.createEligibilityTask(
        trialId,
        mrn,
        targetAction.action_id,
        buildCreateEligibilityTaskRequest(targetAction, taskAssigneeId),
      );
      if (response.outcome === "matched_existing") {
        toast.success(
          "A matching appointment or order is already on the schedule. Status updated to Results Awaiting.",
        );
      } else {
        toast.success("Task initiated for the selected recommended action.");
      }
      refreshDetail?.();
    } catch (reason) {
      toast.error(
        reason instanceof Error
          ? reason.message
          : "Could not initiate task for this action.",
      );
    } finally {
      setTaskSubmitting(false);
    }
  };

  return (
    <div className="action-pending-tab h-full min-h-0">
      <ManualOverrideModal
        isOpen={overrideAtomId !== null}
        atomId={overrideAtomId ?? ""}
        currentAtomStatus={activeAtomStatus}
        existingOverride={activeOverride}
        onClose={() => setOverrideAtomId(null)}
      />

      <EvaluationSplitPane
        heading="Action Pending"
        subheading={`${inclusionGroups.length} criteria`}
        className="bg-white"
        rightPaneClassName="bg-white"
        items={listItems}
        selectedId={selectedQuestionId}
        onSelectedIdChange={setSelectedQuestionId}
      >
        <section className="action-pending--right flex h-full min-h-0 flex-col">
          <div className="flex min-h-0 flex-1 flex-col gap-5 overflow-y-auto p-4">
            <div>
              <div className="mb-3 flex items-center justify-between gap-3">
                <div className="text-sm font-bold text-primaryGray-1">
                  Criteria
                </div>
                <Tags
                  label="Action Pending"
                  backgroundColor={pendingColors.backgroundColor}
                  textColor={pendingColors.textColor}
                />
              </div>
              {selectedGroup ? (
                <InclusionCriteriaAccordion
                  key={selectedGroup.id}
                  title={selectedGroup.title}
                  points={selectedGroup.points}
                  defaultExpanded
                />
              ) : (
                <p className="text-xs text-primaryGray-4">
                  No inclusion criteria are tied to outstanding recommended
                  actions.
                </p>
              )}
            </div>

            <div className="flex flex-col gap-4">
              <div className="text-sm font-bold text-primaryGray-1">
                Recommended CRC Action
              </div>
              {selectedActionCards.length === 0 && (
                <p className="text-xs text-primaryGray-4">
                  No unresolved actionable atoms. Hard FAIL atoms are shown in
                  the evaluation mapping and are not returned as recommended
                  actions.
                </p>
              )}
              {selectedActionCards.map((card) => (
                <CrcActionCard
                  key={card.actionId}
                  name={`crc-action-${card.actionId}`}
                  title={card.title}
                  subtitle={card.subtitle}
                  meta={card.meta}
                  checked={checkedActionIds.has(card.actionId)}
                  onCheckedChange={(checked) =>
                    handleActionChecked(card.actionId, checked)
                  }
                />
              ))}
            </div>
          </div>
          <div className="flex shrink-0 justify-end gap-2 border-t border-primaryGray-14 px-4 py-3">
            <button
              type="button"
              className="rounded border border-primaryGray-14 px-3 py-1.5 text-x-tiny font-medium text-primaryGray-3 transition-all hover:cursor-pointer hover:bg-primaryGray-16 hover:text-primaryGray-1 disabled:cursor-not-allowed disabled:opacity-50"
              disabled={selectedActions.length === 0}
              onClick={handleManualOverrideClick}
            >
              Manually Override
            </button>
            <button
              type="button"
              className="rounded bg-primaryGray-1 px-3 py-1.5 text-x-tiny font-medium text-white transition-opacity hover:cursor-pointer hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
              disabled={
                !hasCheckedAction ||
                taskSubmitting ||
                assigneesLoading ||
                !taskAssigneeId
              }
              onClick={() => void handleTaskInitiatedClick()}
            >
              {taskSubmitting ? "Initiating…" : "Initiate Task"}
            </button>
          </div>
        </section>
      </EvaluationSplitPane>
    </div>
  );
};

export default ActionPendingTab;
